import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Info } from 'lucide-react';
import type { SupplierFormData } from './types';

interface WizardStep1GroupProps {
  formData: SupplierFormData;
  onInputChange: (field: keyof SupplierFormData, value: string | number | boolean | null | string[] | number[]) => void;
  previewMode: boolean;
  supplierGroups: Array<{ id: number; name: string }>;
  onShowCreateGroupModal: () => void;
}

export default function WizardStep1Group({
  formData,
  onInputChange,
  previewMode: _previewMode,
  supplierGroups,
  onShowCreateGroupModal,
}: WizardStep1GroupProps) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold mb-2">Supplier Group</h2>
        <p className="text-muted-foreground">
          Select or create a supplier group.
        </p>
      </div>

      {/* CR-3942: Is supplier part of a group? */}
      <div className="space-y-3">
        <Label>Is this supplier part of a group? <span className="text-red-500">*</span></Label>
        <div className="flex gap-4">
          <Button
            type="button"
            variant={formData.is_part_of_group === true ? 'default' : 'outline'}
            onClick={() => onInputChange('is_part_of_group', true)}
            className="w-24"
          >
            Yes
          </Button>
          <Button
            type="button"
            variant={formData.is_part_of_group === false ? 'default' : 'outline'}
            onClick={() => {
              onInputChange('is_part_of_group', false);
              onInputChange('supplier_group_id', 0);
            }}
            className="w-24"
          >
            No
          </Button>
        </div>
      </div>

      {/* Supplier Group Selection - only shown when Yes */}
      {formData.is_part_of_group === true && (
        <div className="space-y-2">
          <Label htmlFor="supplier_group" className="flex items-center gap-1">
            Supplier Group <span className="text-red-500">*</span>
            <span className="relative group">
              <Info className="h-4 w-4 text-muted-foreground cursor-help" />
              <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 text-xs bg-gray-900 text-white rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                Format: Company name – group
              </span>
            </span>
          </Label>
          <Select
            value={formData.supplier_group_id?.toString()}
            onValueChange={(v) => {
              if (v === 'new') {
                onShowCreateGroupModal();
              } else {
                onInputChange('supplier_group_id', Number(v));
              }
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select supplier group..." />
            </SelectTrigger>
            <SelectContent>
              {supplierGroups.map(group => (
                <SelectItem key={group.id} value={group.id.toString()}>{group.name}</SelectItem>
              ))}
              <SelectItem value="new">+ Create New Group</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
}
